import { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, Checkbox, Autocomplete, Chip, TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

import { procedureSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useCreateProcedureMutation, useGetSpecialitiesQuery } from "../../services/api";
import style from "./procedure.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof procedureSchema.__outputType;
type SpecialityOption = {
  id: number;
  label: string;
  value: string;
};

/**
 * Add Procedure page for creating a new procedure.
 *
 * Notes:
 * - Uses speciality options derived from the API list.
 * - Validation is enforced via `procedureSchema`.
 */
const AddProcedure = () => {
  const [createProcedure] = useCreateProcedureMutation();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const { data: specialityData } = useGetSpecialitiesQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: ""
  });

  const specialities = useMemo<SpecialityOption[]>(
    () =>
      specialityData?.items?.map((speciality: Speciality) => {
        return {
          id: speciality?.id,
          label: speciality?.name,
          value: speciality?.name,
        };
      }) ?? [],
    [specialityData]
  );
  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(procedureSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm<FormState>({
    resolver: yupResolver(procedureSchema),
    mode: "onChange",
  });

  /**
   * Submits the new procedure payload.
   *
   * Args:
   *   values (FormState): Form values for the new procedure.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the create mutation fails.
   */
  const onSubmit = async (values: FormState) => {
    try {
      // Build payload from selected ids; assumes form validation enforces required fields.
      setIsLoading(true);
      const { name, speciality_ids } = values;
      const payload: AddProcedurePayload = {
        name: name,
        speciality_ids: speciality_ids?.map((speciality) => speciality.id) ?? [],
      };

      await createProcedure(payload).unwrap();
      setIsLoading(false);
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Procedure</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new procedure to Pegasus
        Cybersoft.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <Input
              placeholder="e.g Biopsy"
              label="Name"
              required
              register={register}
              name="name"
              errors={errors}
            />
          </Box>
          <Box className={style.row} sx={{ flexDirection: "column" }}>
            <Typography
              className={style.inputLabel}
              sx={{ marginBottom: -2, display: "flex" }}
            >
              Specialities Mapping
              <Typography sx={{ color: "red", marginLeft: 0.7 }}>
                {" *"}
              </Typography>
            </Typography>
            <Controller
              control={control}
              name={"speciality_ids"}
              rules={{ required: true }}
              render={({ field: { onChange }, ...props }) => (
                <Autocomplete<SpecialityOption, true, true, false>
                  {...props}
                  sx={{
                    width: "100%",
                  }}
                  multiple
                  disablePortal
                  disableCloseOnSelect
                  id="tags-outlined"
                  options={specialities}
                  disableClearable={true}
                  onChange={(e, data) => onChange(data)}
                  getOptionLabel={(option: SpecialityOption) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        isEmpty(getValues("speciality_ids"))
                          ? "Select specialities"
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                      error={!!errors.speciality_ids}
                      helperText={
                        errors.speciality_ids
                          ? "Speciality is required"
                          : ""
                      }
                    />
                  )}
                  renderTags={(
                    value: readonly SpecialityOption[],
                    getTagProps
                  ) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.label}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.label}
                    </li>
                  )}
                />
              )}
            />
          </Box>
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigation(-1)}
            label={"Cancel"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AddProcedure;
